import React, { useState } from 'react';
import { X, Image, Mic, Settings, Volume2, Upload, Check, User, Lock, SlidersHorizontal } from 'lucide-react';
import { Slider } from './ui/slider';
import BackgroundTab from './settings/BackgroundTab';

const BodyScanSettingsPanel = ({
  isOpen,
  onClose,
  // Visuel
  bgOpacity,
  onBgOpacityChange,
  menuTransparency,
  onMenuTransparencyChange,
  // Voix
  voiceType,
  onVoiceTypeChange,
  edgeGender,
  onEdgeGenderChange,
  voiceVolume,
  onVoiceVolumeChange,
  customVoices,
  onVoiceUpload,
  uploadingVoice,
  bodyParts,
  // Admin
  isAdmin,
  lockedVoices,
  onToggleLockVoice,
  onDeleteVoice,
  // Background
  backgrounds,
  customVideos,
  selectedBg,
  onBgSelect,
  backgroundImages,
  selectedImageBg,
  onImageBgSelect,
  hoveredVideo,
  onHoverVideo,
  onVideoUpload,
  onVideoRename,
  onVideoToggleLock,
  onVideoDelete,
  defaultVideoId,
  onSetDefaultVideo,
  uploadingVideo,
  onImageUpload,
  uploadingImage,
  selectedAnimation,
  onAnimationChange,
  defaultImageId,
  onSetDefaultImage,
  onImageRename,
  onImageToggleLock,
  onImageDelete
}) => {
  const [activeTab, setActiveTab] = useState('voice');

  if (!isOpen) return null;

  const TABS = [
    { id: 'voice', label: 'Voix', icon: Mic },
    { id: 'background', label: 'Fond', icon: Image },
    { id: 'settings', label: 'Réglages', icon: Settings }
  ];

  return (
    <div className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none">
    <div className="w-[420px] max-h-[80vh] bg-slate-900/98 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl pointer-events-auto panel-slide-in">
      {/* Header */}
      <div className="p-4 border-b border-white/10 bg-gradient-to-r from-indigo-500/10 to-purple-500/10">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-heading font-semibold text-lg flex items-center gap-2">
            <Settings className="w-5 h-5 text-indigo-400" />
            Paramétrer votre séance
          </h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-2 text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                activeTab === tab.id
                  ? 'text-indigo-400 border-b-2 border-indigo-400 bg-indigo-500/10'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="max-h-[60vh] overflow-y-auto">
        {/* Voice Tab */}
        {activeTab === 'voice' && (
          <div className="p-4 space-y-4">
            {/* Choix du type de voix */}
            <div>
              <p className="text-white/60 text-xs uppercase tracking-wider mb-3">Type de voix</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => onVoiceTypeChange('male')}
                  className={`py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1.5 ${
                    voiceType === 'male' ? 'bg-blue-500/30 border border-blue-400/50 text-blue-400' : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                  }`}
                >
                  <User className="w-4 h-4" />
                  Homme
                </button>
                <button
                  onClick={() => onVoiceTypeChange('synthesis')}
                  className={`py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1.5 ${
                    voiceType === 'synthesis' ? 'bg-purple-500/30 border border-purple-400/50 text-purple-400' : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                  }`}
                >
                  <Volume2 className="w-4 h-4" />
                  Synthèse
                </button>
                <button
                  onClick={() => onVoiceTypeChange('female_tahitian')}
                  className={`py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1.5 ${
                    voiceType === 'female_tahitian' ? 'bg-emerald-500/30 border border-emerald-400/50 text-emerald-400' : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                  }`}
                >
                  <Mic className="w-4 h-4" />
                  Tahitienne
                </button>
                <button
                  onClick={() => onVoiceTypeChange('custom')}
                  className={`py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1.5 ${
                    voiceType === 'custom' ? 'bg-pink-500/30 border border-pink-400/50 text-pink-400' : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  Ma Voix
                </button>
                <button
                  onClick={() => onVoiceTypeChange('none')}
                  className={`py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1.5 col-span-2 ${
                    voiceType === 'none' ? 'bg-white/20 border border-white/30 text-white' : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                  }`}
                >
                  <X className="w-4 h-4" />
                  Sans voix
                </button>
              </div>
            </div>

            {/* Volume */}
            {voiceType !== 'none' && (
              <div className="p-3 bg-white/5 rounded-xl">
                <p className="text-white/60 text-xs uppercase tracking-wider mb-3">Volume de la voix</p>
                <div className="flex items-center gap-3">
                  <Volume2 className="w-4 h-4 text-indigo-400" />
                  <Slider
                    value={[voiceVolume * 100]}
                    onValueChange={([v]) => onVoiceVolumeChange(v / 100)}
                    max={100}
                    className="flex-1"
                  />
                  <span className="text-white/60 text-xs w-8">{Math.round(voiceVolume * 100)}%</span>
                </div>
              </div>
            )}

            {/* Upload voix par zone - pour tous les types sauf 'none' */}
            {voiceType === 'synthesis' && (
              <div className="p-3 bg-white/5 rounded-xl" data-testid="edge-voice-selector">
                <p className="text-white/60 text-xs uppercase tracking-wider mb-3">Voix naturelle française</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="edge-voice-female-btn"
                    onClick={() => onEdgeGenderChange && onEdgeGenderChange('female')}
                    className={`py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                      edgeGender === 'female' ? 'bg-purple-500/30 border border-purple-400/50 text-purple-300' : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                    }`}>
                    Denise
                    <span className="text-[10px] opacity-70">Féminine</span>
                  </button>
                  <button
                    data-testid="edge-voice-male-btn"
                    onClick={() => onEdgeGenderChange && onEdgeGenderChange('male')}
                    className={`py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                      edgeGender === 'male' ? 'bg-purple-500/30 border border-purple-400/50 text-purple-300' : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                    }`}>
                    Henri
                    <span className="text-[10px] opacity-70">Masculine</span>
                  </button>
                </div>
                <p className="text-white/40 text-[10px] mt-2">Voix neurales Microsoft — naturelles et gratuites</p>
              </div>
            )}

            {voiceType !== 'none' && voiceType !== 'synthesis' && (
              <div className="p-3 bg-white/5 rounded-xl">
                <p className="text-white/60 text-xs uppercase tracking-wider mb-3">
                  {voiceType === 'female_tahitian' ? 'Voix Tahitienne par zone' : voiceType === 'male' ? 'Voix Homme par zone' : voiceType === 'synthesis' ? 'Synthèse par zone' : 'Mes enregistrements par zone'}
                </p>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {bodyParts.map((part) => {
                    const voiceTypeVoices = customVoices?.[voiceType] || customVoices || {};
                    const hasVoice = voiceTypeVoices[part.id];
                    const isLocked = lockedVoices?.[`${voiceType}_${part.id}`];
                    return (
                      <div key={part.id} className={`p-2 rounded-lg ${hasVoice ? 'bg-white/10' : 'bg-white/5'}`}>
                        <p className="text-white/70 text-xs mb-1">{part.name}</p>
                        {hasVoice ? (
                          <div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1">
                                {isLocked ? <Lock className="w-3 h-3 text-amber-400" /> : <Check className="w-3 h-3 text-green-400" />}
                                <span className={`text-xs ${isLocked ? 'text-amber-400' : 'text-green-400'}`}>{isLocked ? 'Verrouillé' : 'Prêt'}</span>
                              </div>
                              {isAdmin && !isLocked && (
                                <button className="cursor-pointer text-amber-400/60 text-[10px] hover:text-amber-400"
                                  onClick={() => {
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.accept = '.mp3,.wav,.ogg,.m4a';
                                    input.onchange = (e) => { const file = e.target.files[0]; if (file) onVoiceUpload(part.id, file, voiceType); };
                                    input.click();
                                  }}>
                                  Remplacer
                                </button>
                              )}
                            </div>
                            {isAdmin && (
                              <div className="flex gap-1 mt-1">
                                <button onClick={() => onToggleLockVoice && onToggleLockVoice(`${voiceType}_${part.id}`)}
                                  className={`text-[9px] px-1.5 py-0.5 rounded ${isLocked ? 'text-amber-400 bg-amber-500/10' : 'text-white/30 bg-white/5'}`}>
                                  {isLocked ? 'Déverrouiller' : 'Verrouiller'}
                                </button>
                                {!isLocked && onDeleteVoice && (
                                  <button onClick={() => onDeleteVoice(part.id, voiceType)}
                                    className="text-[9px] px-1.5 py-0.5 rounded text-red-400/60 bg-red-500/10 hover:text-red-400">
                                    Supprimer
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        ) : isAdmin ? (
                          <button className="cursor-pointer text-white/40 text-xs flex items-center gap-1"
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = '.mp3,.wav,.ogg,.m4a';
                              input.onchange = (e) => { const file = e.target.files[0]; if (file) onVoiceUpload(part.id, file, voiceType); };
                              input.click();
                            }}>
                            <Upload className="w-3 h-3" /> Ajouter
                          </button>
                        ) : (
                          <span className="text-white/30 text-xs">—</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Background Tab */}
        {activeTab === 'background' && (
          <BackgroundTab
            videos={backgrounds}
            images={backgroundImages}
            selectedBg={selectedBg}
            selectedImageBg={selectedImageBg}
            hoveredVideo={hoveredVideo}
            defaultVideoId={defaultVideoId}
            defaultImageId={defaultImageId}
            uploadingVideo={uploadingVideo}
            uploadingImage={uploadingImage}
            selectedAnimation={selectedAnimation}
            onBgSelect={onBgSelect}
            onImageBgSelect={onImageBgSelect}
            onHoverVideo={onHoverVideo}
            onVideoRename={onVideoRename}
            onVideoToggleLock={onVideoToggleLock}
            onVideoDelete={onVideoDelete}
            onSetDefaultVideo={onSetDefaultVideo}
            onVideoUpload={onVideoUpload}
            onImageRename={onImageRename}
            onImageToggleLock={onImageToggleLock}
            onImageDelete={onImageDelete}
            onSetDefaultImage={onSetDefaultImage}
            onImageUpload={onImageUpload}
            onAnimationChange={onAnimationChange}
            testIdPrefix="bs-"
          />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="p-4 space-y-4">
            <div className="p-3 bg-white/5 rounded-xl space-y-4">
              <p className="text-white/60 text-xs uppercase tracking-wider flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4" />
                Réglages visuels
              </p>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-white/60 text-sm">Opacité du fond</span>
                  <span className="text-white/80 text-sm">{bgOpacity}%</span>
                </div>
                <Slider
                  value={[bgOpacity]}
                  onValueChange={([v]) => onBgOpacityChange(v)}
                  max={80}
                  step={5}
                  className="w-full"
                />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-white/60 text-sm">Transparence menus</span>
                  <span className="text-white/80 text-sm">{menuTransparency}%</span>
                </div>
                <Slider
                  value={[menuTransparency]}
                  onValueChange={([v]) => onMenuTransparencyChange(v)}
                  max={80}
                  step={5}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default BodyScanSettingsPanel;
